import { Button, Input } from "@/components/ui";
import { Typography } from "@/components/ui/Typography/Typography";
import { useUser } from "@/components/UserProvider";
import { useMedicalTreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type Matter, type MedicalTreatmentPlan } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { Check, Plus } from "lucide-react";
import { type MouseEvent, type ReactNode, useMemo, useState } from "react";

interface MatterTreatmentPlansCardProps {
  plans: MedicalTreatmentPlan[];
  contentClassName?: string;
  extraHeaderContent?: ReactNode;
  leftButtons?: ReactNode;
  rightButtons?: ReactNode;
  matter: Matter;
  treatmentId?: string;
  localPlanItems?: { id: string; description: string }[];
  onAddLocalPlanItem?: (description: string) => void;
  onRemoveLocalPlanItem?: (itemId: string) => void;
  locallyCompletedPlanIds?: string[];
  onToggleLocalPlanCompleted?: (planId: string) => void;
}

export function MatterTreatmentPlansCard({
  plans,
  contentClassName = "space-y-3",
  extraHeaderContent,
  leftButtons,
  rightButtons,
  matter,
  localPlanItems = [],
  onAddLocalPlanItem,
  onRemoveLocalPlanItem,
  locallyCompletedPlanIds = [],
  onToggleLocalPlanCompleted,
}: MatterTreatmentPlansCardProps) {
  const { hasActionPermission } = useUser();
  const completeMedicalTreatments = useMedicalTreatmentViewModel(matter);
  const [addingPlanItem, setAddingPlanItem] = useState(false);
  const [newPlanItemText, setNewPlanItemText] = useState("");

  const sortedPlans = useMemo(() => {
    const treatmentOrderById = new Map(
      completeMedicalTreatments.map((treatment, index) => [
        treatment.id,
        index,
      ]),
    );
    const planTreatmentIdByPlanId = new Map<string, string>();

    completeMedicalTreatments.forEach((treatment) => {
      treatment.plans.forEach((plan) => {
        planTreatmentIdByPlanId.set(plan.id, treatment.id);
      });
    });

    matter.medicalTreatmentAppointmentConnections.forEach((connection) => {
      const connectionPlanId = connection.medicalTreatmentPlanId;
      const connectionTreatmentId = connection.medicalTreatmentId;
      if (
        typeof connectionPlanId === "string" &&
        connectionPlanId.length > 0 &&
        typeof connectionTreatmentId === "string" &&
        !planTreatmentIdByPlanId.has(connectionPlanId)
      ) {
        planTreatmentIdByPlanId.set(connectionPlanId, connectionTreatmentId);
      }
    });

    const incomplete = plans.filter((plan) => plan.status !== "Completed");

    const sortKeyForPlan = (plan: MedicalTreatmentPlan) => {
      const treatmentId = planTreatmentIdByPlanId.get(plan.id);
      const treatmentOrder = treatmentId
        ? (treatmentOrderById.get(treatmentId) ?? Number.MAX_SAFE_INTEGER)
        : Number.MAX_SAFE_INTEGER;
      return {
        treatmentOrder,
        date: plan.date,
        id: plan.id,
      };
    };

    return [...incomplete].sort((a, b) => {
      const ka = sortKeyForPlan(a);
      const kb = sortKeyForPlan(b);
      if (ka.treatmentOrder !== kb.treatmentOrder) {
        return ka.treatmentOrder - kb.treatmentOrder;
      }
      const dateCmp = ka.date.localeCompare(kb.date);
      if (dateCmp !== 0) {
        return dateCmp;
      }
      return ka.id.localeCompare(kb.id);
    });
  }, [
    completeMedicalTreatments,
    matter.medicalTreatmentAppointmentConnections,
    plans,
  ]);

  const hasPlans = sortedPlans.length > 0;
  const canAddPlanItem =
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterMedicalTreatmentPlan,
      ACTION_PERMISSION_VALUES.create,
    ) && Boolean(onAddLocalPlanItem);
  const hasListRows = localPlanItems.length > 0 || hasPlans;

  const createInlinePlan = () => {
    const trimmedDescription = newPlanItemText.trim();
    if (!trimmedDescription) {
      return;
    }

    if (onAddLocalPlanItem) {
      onAddLocalPlanItem(trimmedDescription);
      setNewPlanItemText("");
      setAddingPlanItem(false);
      return;
    }
  };

  const togglePlanCompleted = (plan: MedicalTreatmentPlan) => {
    if (plan.status === "Completed") {
      return;
    }
    onToggleLocalPlanCompleted?.(plan.id);
  };

  return (
    <div className={cn("space-y-4", contentClassName)}>
      <div className="flex items-center justify-between gap-3">
        <Typography variant="default">Treatment plan</Typography>
        <div className="flex items-center gap-2">
          {leftButtons}
          {rightButtons}
          {extraHeaderContent}
        </div>
      </div>

      {(hasListRows || canAddPlanItem) && (
        <div className="space-y-5">
          {(hasListRows || (canAddPlanItem && addingPlanItem)) && (
            <ul className="space-y-5">
              {localPlanItems.map((item) => (
                <li
                  key={item.id}
                  className="text-foreground flex items-start gap-3"
                >
                  <div className="border-border mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-sm border border-neutral-300 bg-transparent" />
                  <div className="min-w-0 flex-1">
                    <Typography
                      variant="subHeading"
                      className="text-foreground"
                    >
                      {item.description}
                    </Typography>
                    <Typography variant="subHeading" tag="p" className="mt-0.5">
                      Added this visit
                    </Typography>
                  </div>
                  {onRemoveLocalPlanItem && (
                    <button
                      type="button"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        onRemoveLocalPlanItem(item.id);
                      }}
                      className="text-destructive hover:text-destructive/80 shrink-0 text-[13px] leading-5 font-medium transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
              {sortedPlans.map((plan) => {
                const isChecked =
                  plan.status === "Completed" ||
                  locallyCompletedPlanIds.includes(plan.id);

                return (
                  <li key={plan.id}>
                    <button
                      type="button"
                      className="text-foreground hover:text-foreground/90 flex w-full cursor-pointer items-start gap-3 text-left transition-colors"
                      onClick={() => {
                        togglePlanCompleted(plan);
                      }}
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-sm border transition-colors",
                          isChecked
                            ? "border-emerald-600 bg-emerald-600"
                            : "border-neutral-300 bg-transparent",
                        )}
                        aria-hidden
                      >
                        {isChecked && (
                          <Check
                            className="h-3.5 w-3.5 text-white"
                            strokeWidth={3}
                          />
                        )}
                      </span>
                      <Typography
                        variant="subHeading"
                        className="text-foreground"
                      >
                        {plan.description?.trim() || "Treatment plan item"}
                      </Typography>
                    </button>
                  </li>
                );
              })}
              {canAddPlanItem && addingPlanItem && (
                <li className="text-foreground">
                  <div className="flex w-full items-start gap-3">
                    <div
                      className="border-border mt-0.5 flex h-[18px] w-[18px] shrink-0 rounded-sm border border-neutral-300 bg-transparent"
                      aria-hidden
                    />
                    <div className="flex min-w-0 flex-1 flex-col items-stretch gap-1">
                      <Input
                        autoFocus
                        value={newPlanItemText}
                        onChange={(e) => {
                          setNewPlanItemText(e.target.value);
                        }}
                        placeholder="New treatment plan item..."
                        className="text-foreground placeholder:text-muted-foreground h-7 w-full min-w-0 rounded-md border-neutral-300 bg-transparent px-2 py-0 text-xs leading-4 shadow-none focus-visible:ring-1 focus-visible:ring-offset-0 md:text-xs"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (newPlanItemText.trim()) {
                              createInlinePlan();
                            }
                          }
                          if (e.key === "Escape") {
                            setNewPlanItemText("");
                            setAddingPlanItem(false);
                          }
                        }}
                      />
                      <div className="flex w-full flex-wrap items-center justify-start gap-1.5">
                        <Button
                          type="button"
                          size="xs"
                          className="h-6 rounded-md px-2 text-xs leading-4 font-medium"
                          disabled={!newPlanItemText.trim()}
                          onClick={() => {
                            createInlinePlan();
                          }}
                        >
                          Add
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          className="text-muted-foreground hover:text-foreground h-6 px-1.5 text-xs leading-4 font-medium"
                          onClick={() => {
                            setNewPlanItemText("");
                            setAddingPlanItem(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              )}
            </ul>
          )}

          {canAddPlanItem && !addingPlanItem && (
            <button
              type="button"
              onClick={() => {
                setAddingPlanItem(true);
              }}
              className="text-muted-foreground hover:text-foreground/70 flex items-center gap-1 py-0.5 text-xs leading-4 transition-colors"
            >
              <Plus className="h-3 w-3 shrink-0 stroke-[1.75]" />
              Add item
            </button>
          )}
        </div>
      )}
    </div>
  );
}
